// LT-413 (LT-405 §8.4): Campaign Brief card on the cockpit left rail.
//
// Replaces the legacy ai_context card. Same single-textarea UX (headers
// parse back to structured fields), updated to the new shape:
//
//   Why we're calling (purpose), Target audience, Call objectives,
//   Likely objections, Scripts, Additional notes
//
// Tone is L1 now (lives on the agent) and is NOT part of this surface.
//
// Reads from the bridge-view ai_context for historical campaigns AND
// from the structured columns when populated. Saves via the structured
// fields directly — `ai_context` writes are 422'd by the backend per
// LT-409.
import React, { useMemo, useState } from 'react';
import { Edit2 } from 'lucide-react';

import { Button, Callout, Card, ConfirmReasonModal, Pill, Textarea } from '../../../ui';
import {
  campaignsAPI,
  extractCampaignApiError,
} from '../../../services/campaignsAPI';
import type { Campaign, CampaignUpdatePayload } from '../../../types/campaign';
import {
  CAMPAIGN_BRIEF_BUDGET,
  estimateBriefRenderedLength,
  normalizeBriefFromCampaign,
  type CampaignBriefValue,
} from '../CampaignAiContext';

interface CampaignAiContextCardProps {
  campaign: Campaign;
  /** True when the campaign hasn't been launched — copy explains the
   * Layer-2 edit will affect every snapshotted enrolment at launch. */
  isPreLaunch: boolean;
  /** Number of enrolments currently dispatching — used in the
   * "this affects N in-flight enrolments" callout. */
  activeEnrolments?: number | null;
  onCampaignChange: (next: Campaign) => void;
  onAuditChanged: () => void;
}

function valueToText(v: CampaignBriefValue): string {
  const lines: string[] = [];
  if (v.purpose) lines.push(`Purpose: ${v.purpose}`);
  if (v.target_audience) lines.push(`Target audience: ${v.target_audience}`);
  if (v.call_objectives) lines.push(`Call objectives: ${v.call_objectives}`);
  if (v.likely_objections && v.likely_objections.length > 0) {
    lines.push(`Likely objections:\n${v.likely_objections.map((o) => `  - ${o}`).join('\n')}`);
  }
  if (v.scripts && v.scripts.length > 0) {
    lines.push(`Scripts:\n${v.scripts.map((s) => `  - ${s}`).join('\n')}`);
  }
  if (v.additional_notes) lines.push(`Additional notes: ${v.additional_notes}`);
  return lines.join('\n\n');
}

/** Reverse of valueToText. The cockpit editor is intentionally a single
 * textarea (Layer-2 free-form) — operators pick structure when they
 * need it. We parse the headers back out so the API gets typed fields. */
function textToValue(s: string): CampaignBriefValue {
  const out: CampaignBriefValue = {};
  const sections = s.split(/\n\s*\n/);
  for (const section of sections) {
    const trimmed = section.trim();
    if (!trimmed) continue;
    const colon = trimmed.indexOf(':');
    if (colon === -1) {
      // Treat unstructured paragraph as the purpose.
      if (!out.purpose) out.purpose = trimmed;
      continue;
    }
    const head = trimmed.slice(0, colon).toLowerCase().trim();
    const body = trimmed.slice(colon + 1).trim();
    if (head.startsWith('purpose') || head.startsWith('why')) {
      out.purpose = body;
    } else if (head.startsWith('target audience') || head.startsWith('audience') || head.startsWith('target persona')) {
      out.target_audience = body;
    } else if (head.startsWith('call objectives') || head.startsWith('objectives')) {
      out.call_objectives = body;
    } else if (head.startsWith('likely objections') || head.startsWith('common objections') || head.startsWith('objections')) {
      out.likely_objections = body
        .split('\n')
        .map((l: string) => l.replace(/^[-*•]\s*/, '').trim())
        .filter(Boolean);
    } else if (head.startsWith('scripts')) {
      out.scripts = body
        .split('\n')
        .map((l: string) => l.replace(/^[-*•]\s*/, '').trim())
        .filter(Boolean);
    } else if (head.startsWith('additional notes') || head.startsWith('notes')) {
      out.additional_notes = body;
    } else if (!out.purpose) {
      out.purpose = trimmed;
    }
  }
  return out;
}

const CampaignAiContextCard: React.FC<CampaignAiContextCardProps> = ({
  campaign,
  isPreLaunch,
  activeEnrolments,
  onCampaignChange,
  onAuditChanged,
}) => {
  const value = useMemo(
    () => normalizeBriefFromCampaign(campaign),
    [campaign],
  );

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(valueToText(value));
    setEditing(true);
    setError(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };

  const submitEdit = () => {
    const next = textToValue(draft);
    const used = estimateBriefRenderedLength(next);
    if (used > CAMPAIGN_BRIEF_BUDGET) {
      setError(
        `Combined length is ${used}, over the ${CAMPAIGN_BRIEF_BUDGET}-char budget. Trim the entry.`,
      );
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const confirmEdit = async (reason: string) => {
    setBusy(true);
    setError(null);
    try {
      const next = textToValue(draft);
      // LT-413: send the structured columns directly (LT-409 422s any
      // ai_context payload). Pass null for fields cleared by the edit so
      // the backend can also unset them.
      const patch: CampaignUpdatePayload = { reason };
      patch.purpose = next.purpose ?? '';
      patch.target_audience = next.target_audience ?? '';
      patch.call_objectives = next.call_objectives ?? '';
      patch.likely_objections = next.likely_objections ?? [];
      patch.scripts = next.scripts ?? [];
      patch.additional_notes = next.additional_notes ?? '';
      const updated = await campaignsAPI.update(campaign.id, patch);
      onCampaignChange(updated);
      onAuditChanged();
      setConfirmOpen(false);
      setEditing(false);
      setDraft('');
    } catch (e) {
      setError(extractCampaignApiError(e, 'Could not save campaign brief.'));
    } finally {
      setBusy(false);
    }
  };

  const empty =
    !value.purpose
    && !value.target_audience
    && !value.call_objectives
    && (!value.likely_objections || value.likely_objections.length === 0)
    && (!value.scripts || value.scripts.length === 0)
    && !value.additional_notes;

  const used = estimateBriefRenderedLength(textToValue(draft));
  const overBudget = used > CAMPAIGN_BRIEF_BUDGET;

  return (
    <Card padding="tight">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
            Campaign Brief · Layer 2
          </div>
        </div>
        {!editing ? (
          <Button intent="ghost" size="sm" onClick={startEdit}>
            <Edit2 size={12} strokeWidth={2.5} />
            Edit
          </Button>
        ) : null}
      </div>

      {editing ? (
        <>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            placeholder={
              'Purpose: …\nTarget audience: …\nCall objectives: …\n\nLikely objections:\n  - …\n\nScripts:\n  - …'
            }
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] font-mono text-slate-500">
              {used} / {CAMPAIGN_BRIEF_BUDGET}
            </span>
            {overBudget ? (
              <Pill intent="danger">over budget</Pill>
            ) : null}
          </div>
          <Callout
            tone="info"
            title={
              isPreLaunch
                ? 'Will affect every contact at launch'
                : `Will affect ${activeEnrolments ?? 0} in-flight enrolment${activeEnrolments === 1 ? '' : 's'}`
            }
            className="mt-2"
          >
            Layer 2 edits apply to the next call composed for any contact
            in this campaign — not retroactively to calls already made. An
            audit entry is written with your reason.
          </Callout>
          <div className="flex gap-2 mt-3">
            <Button intent="primary" size="sm" onClick={submitEdit}>
              Save
            </Button>
            <Button intent="ghost" size="sm" onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
          {error ? (
            <div className="mt-2 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-xs">
              {error}
            </div>
          ) : null}
        </>
      ) : empty ? (
        <p className="text-xs text-slate-500 italic">
          No campaign brief written yet. The agent uses the workspace
          defaults until you set this.
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap mb-2">
            {value.purpose ?? ''}
          </p>
          <div className="flex flex-wrap gap-1">
            {value.purpose ? <Pill intent="info">purpose</Pill> : null}
            {value.target_audience ? <Pill intent="info">audience</Pill> : null}
            {value.call_objectives ? <Pill intent="info">objectives</Pill> : null}
            {value.likely_objections && value.likely_objections.length > 0 ? (
              <Pill intent="info">objections · {value.likely_objections.length}</Pill>
            ) : null}
            {value.scripts && value.scripts.length > 0 ? (
              <Pill intent="info">scripts · {value.scripts.length}</Pill>
            ) : null}
            {value.additional_notes ? <Pill intent="info">notes</Pill> : null}
          </div>
        </>
      )}

      <ConfirmReasonModal
        open={confirmOpen}
        title="Save campaign brief edit?"
        description={
          <>
            Future calls in this campaign will pick up the new Layer-2
            brief. Calls already composed are unaffected. A reason is
            required so the change is auditable.
          </>
        }
        confirmLabel="Save brief"
        confirmIntent="primary"
        saving={busy}
        error={error}
        onCancel={() => {
          if (!busy) setConfirmOpen(false);
        }}
        onConfirm={confirmEdit}
      />
    </Card>
  );
};

export default CampaignAiContextCard;
